import {BackendApi} from '../api/api.ts'

export type AdministrationOption = {
    ref: string
    name: string
    number: string
    employerRef: string
    employerName: string
}

export type LinkedAdministration = {
    administrationId: string
    businessId: string
    connectionId: string
    externalPayrollRef: string
    externalEmployerRef: string
    administrationName: string
    administrationNumber: string
}

export type ClientAdministration = {
    administrationId: string
    externalPayrollRef: string
    externalEmployerRef: string | null
    administrationName: string | null
    administrationNumber: string
    mappingId: string | null
    mappingName: string | null
}

export type GLAccountRef = {
    code: string
    description: string
}

export type MappingCoverage = {
    administrationId: string
    mappingId: string
    allGlAccounts: GLAccountRef[]
    unmappedGlAccounts: GLAccountRef[]
    runsCovered: number
    runsTotal: number
}

export async function listAdministrationOptions(
    api: BackendApi,
    signal?: AbortSignal,
): Promise<AdministrationOption[]> {
    const response = await api.withAbortSignal(signal).Partner__payroll__administrationOptions(null)
    return response.administrations.map(a => ({
        ref: a.ref,
        name: a.name,
        number: a.number,
        employerRef: a.employer_ref,
        employerName: a.employer_name,
    }))
}

export async function getClientAdministration(
    api: BackendApi,
    businessId: string,
): Promise<ClientAdministration | null> {
    const response = await api.Partner__payroll__getPayrollAdministration(businessId)
    if (!response.administration_id) {
        return null
    }
    return {
        administrationId: response.administration_id,
        externalPayrollRef: response.external_payroll_ref ?? '',
        externalEmployerRef: response.external_employer_ref ?? null,
        administrationName: response.administration_name ?? null,
        administrationNumber: response.administration_number ?? '',
        mappingId: response.mapping_id ?? null,
        mappingName: response.mapping_name ?? null,
    }
}

export async function linkAdministration(
    api: BackendApi,
    clientBusinessId: string,
    employerRef: string,
    administrationRef: string,
): Promise<LinkedAdministration> {
    const response = await api.Partner__payroll__linkPayrollAdministration({
        client_business_id: clientBusinessId,
        employer_ref: employerRef,
        administration_ref: administrationRef,
    })
    return {
        administrationId: response.administration_id,
        businessId: response.business_id,
        connectionId: response.connection_id,
        externalPayrollRef: response.external_payroll_ref,
        externalEmployerRef: response.external_employer_ref,
        administrationName: response.administration_name,
        administrationNumber: response.administration_number ?? '',
    }
}

export async function assignMapping(
    api: BackendApi,
    administrationId: string,
    mappingId: string,
): Promise<{administrationId: string; mappingId: string}> {
    const response = await api.Partner__payroll__administrations__assignMapping({
        administration_id: administrationId,
        mapping_id: mappingId,
    })
    return {
        administrationId: response.administration_id,
        mappingId: response.mapping_id,
    }
}

export async function getMappingCoverage(
    api: BackendApi,
    administrationId: string,
    periodStart: string,
    signal?: AbortSignal,
): Promise<MappingCoverage> {
    const response = await api.withAbortSignal(signal).Partner__payroll__administrations__mappingCoverage(
        administrationId,
        periodStart,
    )
    return {
        administrationId: response.administration_id,
        mappingId: response.mapping_id,
        allGlAccounts: response.all_gl_accounts.map(a => ({code: a.code, description: a.description})),
        unmappedGlAccounts: response.unmapped_gl_accounts.map(a => ({code: a.code, description: a.description})),
        runsCovered: response.runs_covered,
        runsTotal: response.runs_total,
    }
}

export function partialCoverageNote(coverage: MappingCoverage): string | null {
    if (coverage.runsTotal === 0 || coverage.runsCovered >= coverage.runsTotal) return null
    return `Op basis van ${coverage.runsCovered} van ${coverage.runsTotal} runs`
}
